import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, MessageCircle, Play } from "lucide-react";
import { useApi, img, waLink } from "@/lib/api";
import { InquiryForm } from "@/components/site/InquiryForm";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";

export const VIDEOS = ["JaLnVTOHXps", "J7z8BTvca4I", "ISOuIRgt4fs"];
const ARTICLE = "https://trinityrealestatenicaragua.com/a-canadian-cowgirl-turned-developer-in-nicaragua/";
const thumb = (id) => `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;
export const bigThumb = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
export const fallback = (id) => (e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = 1; e.currentTarget.src = thumb(id); } };

export const VideoSlider = ({ className = "container-x pt-10", title, testId = "about-videos" }) => {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const go = (n) => { setI((n + VIDEOS.length) % VIDEOS.length); setPlaying(false); };
  const id = VIDEOS[i];
  const arrow = "absolute top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-forest shadow-lg transition-transform duration-200 hover:scale-110";
  return (
    <section className={className} data-testid={testId}>
      {title && <h2 className="mb-6 text-3xl font-black tracking-tight text-forest sm:text-4xl">{title}</h2>}
      <div className="relative overflow-hidden rounded-3xl bg-ink aspect-video">
        {playing ? (
          <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`} title={t("about.videoTitle", { n: i + 1 })}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen data-testid="about-video-player" />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0" aria-label={t("about.play")} data-testid={`about-video-play-${i}`}>
            <img src={bigThumb(id)} onError={fallback(id)} alt={t("about.videoTitle", { n: i + 1 })} className="h-full w-full object-cover" />
            <span className="absolute inset-0 bg-black/25 transition-colors duration-200 group-hover:bg-black/10" />
            <span className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-forest shadow-xl transition-transform duration-200 group-hover:scale-110"><Play className="ml-1 h-8 w-8 fill-current" /></span>
          </button>
        )}
        <button type="button" onClick={() => go(i - 1)} className={`${arrow} left-3 sm:left-5`} aria-label="Previous video" data-testid="about-video-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" onClick={() => go(i + 1)} className={`${arrow} right-3 sm:right-5`} aria-label="Next video" data-testid="about-video-next"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-4">
        {VIDEOS.map((v, n) => (
          <button key={v} type="button" onClick={() => go(n)} data-testid={`about-video-thumb-${n}`}
            className={`relative overflow-hidden rounded-xl aspect-video ring-offset-2 transition-opacity duration-200 ${n === i ? "ring-2 ring-forest" : "opacity-60 hover:opacity-100"}`}>
            <img src={thumb(v)} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </section>
  );
};

const Bio = ({ photo }) => {
  const { t, to } = useLang();
  return (
    <section className="container-x grid gap-12 py-20 sm:py-24 lg:grid-cols-12" data-testid="about-bio">
      <Reveal className="lg:col-span-5">
        <img src={photo ? img(photo) : bigThumb(VIDEOS[0])} onError={photo ? undefined : fallback(VIDEOS[0])} alt="Heather Anne van Doorninck (Blue) — Escamequita, Nicaragua" className="aspect-[4/5] w-full rounded-3xl object-cover object-[50%_35%]" data-testid="about-photo" />
      </Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <p className="eyebrow">{t("about.eyebrow")}</p>
        <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-forest sm:text-5xl" data-testid="about-title">{t("about.title")}</h1>
        <div className="mt-8 space-y-5 text-base font-medium leading-relaxed text-moss md:text-lg" data-testid="about-text">
          {t("about.paras").map((p) => <p key={p}>{p}</p>)}
          <p className="font-display text-2xl font-black text-forest">{t("about.closing")}</p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={to("/contact")} className="btn-primary" data-testid="about-contact-cta">{t("about.cta")}</Link>
          <Link to={to("/homes-and-land-for-sale")} className="btn-outline" data-testid="about-homes-cta">{t("nav.homes")}</Link>
        </div>
        <a href={ARTICLE} target="_blank" rel="noreferrer" className="mt-10 flex items-center justify-between gap-4 rounded-2xl border border-clay bg-linen p-6 transition-colors duration-200 hover:border-forest" data-testid="about-article-link">
          <span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sage">{t("about.featured")}</span>
            <span className="mt-1 block font-display text-xl font-black text-ink">A Canadian Cowgirl turned developer in Nicaragua</span>
            <span className="mt-1 block text-sm font-semibold text-moss">Trinity Real Estate Nicaragua</span>
          </span>
          <ArrowUpRight className="h-5 w-5 shrink-0 text-forest" />
        </a>
      </Reveal>
    </section>
  );
};

const TalkToBlue = ({ s }) => {
  const { t } = useLang();
  return (
    <section className="container-x pb-28" data-testid="about-contact">
      <div className="grid gap-12 rounded-3xl border border-clay bg-linen p-8 sm:p-12 lg:grid-cols-2 lg:p-16">
        <Reveal>
          <p className="eyebrow">{t("about.talkEyebrow")}</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-forest sm:text-4xl">{t("about.talkTitle")}</h2>
          <p className="mt-5 font-medium leading-relaxed text-moss">{t("about.talkText")}</p>
          {s?.contact_whatsapp && (
            <a href={waLink(s.contact_whatsapp, t("about.waMsg"))} target="_blank" rel="noreferrer" data-testid="about-whatsapp-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-extrabold text-white shadow-lg transition-transform duration-200 hover:scale-105">
              <MessageCircle className="h-5 w-5" /> {t("about.waCta")}
            </a>
          )}
          {s?.contact_phone_display && <p className="mt-3 text-sm font-semibold text-moss" data-testid="about-phone">{s.contact_phone_display}</p>}
        </Reveal>
        <Reveal delay={0.1}><InquiryForm /></Reveal>
      </div>
    </section>
  );
};

export default function About() {
  const { t } = useLang();
  const { data: s } = useApi("/settings");
  return (
    <>
      <Seo title={t("about.seoTitle")} description={t("about.seoDesc")} image={s?.about_photo || thumb(VIDEOS[0])}
        jsonLd={{ "@context": "https://schema.org", "@type": "Person", name: "Heather Anne van Doorninck", alternateName: "Blue", jobTitle: "Real estate broker and developer", homeLocation: "Escamequita, Rivas, Nicaragua", url: "https://www.escamequita.com/about" }} />
      <VideoSlider />
      <Bio photo={s?.about_photo} />
      <TalkToBlue s={s} />
    </>
  );
}
